// "Get Pro" pressed while signed out: remember the choice across the sign-in
// round trip (Google OAuth leaves the page and comes back to the same path in
// the same tab, which is exactly sessionStorage's scope) and finish it once
// the session exists. Stale after 30 minutes — a checkout nobody remembers
// asking for is worse than one more click.
import {
  isBillingInterval,
  isPaidPlanId,
  type BillingInterval,
  type PaidPlan,
} from "@/lib/billing/planConfig";

const KEY = "steampeek:pending-plan";
const TTL_MS = 30 * 60 * 1000;

export type PendingPlanIntent = { plan: PaidPlan; interval: BillingInterval; source: string };

export function savePendingPlanIntent(intent: PendingPlanIntent): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...intent, at: Date.now() }));
  } catch {
    // storage blocked — the user just clicks again after signing in
  }
}

/** Forget a pending choice — the user closed the sign-in modal without signing in. */
export function clearPendingPlanIntent(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}

/** Reads AND clears the intent, so it can only fire once. */
export function takePendingPlanIntent(now = Date.now()): PendingPlanIntent | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (raw) sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (!isPaidPlanId(v.plan) || !isBillingInterval(v.interval)) return null;
    if (typeof v.at !== "number" || now - v.at > TTL_MS) return null;
    return { plan: v.plan, interval: v.interval, source: typeof v.source === "string" ? v.source : "unknown" };
  } catch {
    return null;
  }
}
